import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { auth } from '../firebase';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { billingAPI, notificationsAPI, reportsAPI, googleAdsAPI, campaignsAPI } from '../utils/api';

const API = process.env.REACT_APP_API_URL || 'https://clicksaver.app';

// Lemon Squeezy variant IDs
const PRICE_STARTER  = process.env.REACT_APP_LS_VARIANT_STARTER  || '';
const PRICE_PRO      = process.env.REACT_APP_LS_VARIANT_PRO      || '';
const PRICE_ADVANCED = process.env.REACT_APP_LS_VARIANT_ADVANCED || '';

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    basePrice: 69,
    priceId: PRICE_STARTER,
    features: ['Up to 10,000 clicks/month', '1 campaign', 'Core fraud detection signals', 'Google Ads IP exclusions', 'Real-time dashboard', 'Email reports'],
  },
  {
    id: 'pro',
    name: 'Pro',
    basePrice: 109,
    priceId: PRICE_PRO,
    badge: 'Most Popular',
    features: ['Up to 50,000 clicks/month', '3 campaigns', 'All fraud detection signals', 'Google Ads IP exclusions', 'Real-time dashboard', 'Email reports', 'Priority support'],
  },
  {
    id: 'advanced',
    name: 'Advanced',
    basePrice: 139,
    priceId: PRICE_ADVANCED,
    features: ['Unlimited clicks/month', 'Unlimited campaigns', 'All fraud detection signals', 'Google Ads IP exclusions', 'Real-time dashboard', 'Email reports', 'Priority support', 'Custom detection rules'],
  },
];

const PROMO_CODES = {
  CLICK10:  { percent: 10,  label: '10% off' },
  CLICK50:  { percent: 50,  label: '50% off' },
  CLICK100: { percent: 100, label: '100% off' },
};

const applyPromoPrice = (base, percent) => {
  const discounted = base * (1 - percent / 100);
  return `$${discounted % 1 === 0 ? discounted.toFixed(0) : discounted.toFixed(2)}`;
};

async function apiGet(path) {
  const token = await auth.currentUser?.getIdToken();
  return axios.get(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.data);
}
async function apiPatch(path, data) {
  const token = await auth.currentUser?.getIdToken();
  return axios.patch(`${API}${path}`, data, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.data);
}
async function apiPost(path, data) {
  const token = await auth.currentUser?.getIdToken();
  return axios.post(`${API}${path}`, data, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.data);
}
async function apiDelete(path) {
  const token = await auth.currentUser?.getIdToken();
  return axios.delete(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.data);
}

const TABS = [
  { id: 'account', label: 'Account Settings' },
  { id: 'billing', label: 'Billing & Domains' },
];

const TIMEZONES = [
  '(UTC-12:00) International Date Line West',
  '(UTC-11:00) Samoa',
  '(UTC-10:00) Hawaii',
  '(UTC-09:00) Alaska',
  '(UTC-08:00) Pacific Time (US & Canada)',
  '(UTC-07:00) Mountain Time (US & Canada)',
  '(UTC-06:00) Central Time (US & Canada)',
  '(UTC-05:00) Eastern Time (US & Canada)',
  '(UTC-04:00) Atlantic Time (Canada)',
  '(UTC-03:00) Buenos Aires',
  '(UTC-02:00) Mid-Atlantic',
  '(UTC-01:00) Azores',
  '(UTC+00:00) UTC',
  '(UTC+00:00) London, Dublin',
  '(UTC+01:00) Berlin, Paris, Rome',
  '(UTC+02:00) Cairo, Helsinki',
  '(UTC+03:00) Moscow, Kuwait',
  '(UTC+03:30) Tehran',
  '(UTC+04:00) Dubai, Baku',
  '(UTC+04:30) Kabul',
  '(UTC+05:00) Islamabad, Karachi',
  '(UTC+05:30) Chennai, Mumbai, New Delhi',
  '(UTC+05:45) Kathmandu',
  '(UTC+06:00) Dhaka, Almaty',
  '(UTC+06:30) Yangon',
  '(UTC+07:00) Bangkok, Jakarta',
  '(UTC+08:00) Singapore, Perth, Beijing',
  '(UTC+09:00) Tokyo, Seoul',
  '(UTC+09:30) Adelaide, Darwin',
  '(UTC+10:00) Canberra, Melbourne, Sydney',
  '(UTC+11:00) Solomon Islands',
  '(UTC+12:00) Auckland, Wellington',
  '(UTC+13:00) Nuku\'alofa',
];

const THEMES = [
  { id: 'light', label: 'Light', desc: 'Clean and bright', colors: { bg: '#f6f8fa', card: '#fff', border: '#d0d7de', text: '#1f2328', accent: '#0969da' } },
  { id: 'dark', label: 'Dark', desc: 'Easy on the eyes', colors: { bg: '#080c10', card: '#111820', border: '#1e2d3d', text: '#e6edf3', accent: '#58a6ff' } },
  { id: 'purple', label: 'Purple', desc: 'Light purple theme', colors: { bg: '#f5f0ff', card: '#fff', border: '#d4c5f9', text: '#2d1b69', accent: '#7c3aed' } },
  { id: 'dark-purple', label: 'Dark Purple', desc: 'Deep purple night', colors: { bg: '#13091f', card: '#21133a', border: '#3d2666', text: '#e8dff5', accent: '#a78bfa' } },
];

const LANGUAGES = ['English', 'Spanish', 'French', 'German', 'Portuguese', 'Chinese', 'Japanese', 'Korean', 'Arabic', 'Hindi'];

/* ── Toggle ── */
function Toggle({ value, onChange }) {
  return (
    <button onClick={() => onChange(!value)} style={{
      width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
      background: value ? 'var(--accent-green)' : 'var(--border)',
      transition: 'background 0.2s', position: 'relative', flexShrink: 0,
    }}>
      <div style={{
        position: 'absolute', top: 3, left: value ? 22 : 3,
        width: 18, height: 18, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </button>
  );
}

/* ════════════════════════════════════════
   TAB 1: ACCOUNT SETTINGS
   ════════════════════════════════════════ */
function AccountTab({ theme, setTheme }) {
  // Password
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwMsg, setPwMsg] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);

  // Account fields
  const [phone, setPhone] = useState('');
  const [timezone, setTimezone] = useState('(UTC+00:00) UTC');
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [acctSaving, setAcctSaving] = useState(false);
  const [acctSaved, setAcctSaved] = useState(false);

  useEffect(() => {
    apiGet('/api/auth/account').then(d => {
      if (d.phone) setPhone(d.phone);
      if (d.timezone) setTimezone(d.timezone);
      if (d.api_key) setApiKey(d.api_key);
    }).catch(() => {});
  }, []);

  const handleChangePassword = async () => {
    if (!currentPw || !newPw) return setPwMsg({ type: 'error', text: 'Fill in all password fields' });
    if (newPw !== confirmPw) return setPwMsg({ type: 'error', text: 'New passwords do not match' });
    if (newPw.length < 6) return setPwMsg({ type: 'error', text: 'Password must be at least 6 characters' });
    setPwSaving(true);
    setPwMsg(null);
    try {
      const user = auth.currentUser;
      const cred = EmailAuthProvider.credential(user.email, currentPw);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, newPw);
      setPwMsg({ type: 'success', text: 'Password changed successfully' });
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (e) {
      setPwMsg({ type: 'error', text: e.code === 'auth/wrong-password' ? 'Current password is incorrect' : e.message });
    }
    setPwSaving(false);
  };

  const handleSaveAccount = async () => {
    setAcctSaving(true);
    try {
      await apiPatch('/api/auth/account', { phone, timezone });
      setAcctSaved(true);
      setTimeout(() => setAcctSaved(false), 2500);
    } catch (_) {}
    setAcctSaving(false);
  };

  const handleRegenApiKey = async () => {
    if (!window.confirm('Regenerate your API key? The old key will stop working immediately.')) return;
    try {
      const r = await apiPost('/api/auth/api-key/regenerate');
      setApiKey(r.api_key);
    } catch (_) {}
  };

  const handleThemeChange = (id) => {
    setTheme(id);
  };

  return (
    <>
      {/* Change Password */}
      <div className="card" style={{ maxWidth: 700, marginBottom: 12 }}>
        <div className="section-title">Change Password</div>

        <div className="form-group" style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>Current Password</label>
          <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)}
            placeholder="Enter current password"
            style={{ width: '100%', padding: '10px 14px', fontSize: 13, background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 8 }} />
        </div>

        <div className="form-group" style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>New Password</label>
          <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
            placeholder="Enter new password"
            style={{ width: '100%', padding: '10px 14px', fontSize: 13, background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 8 }} />
        </div>

        <div className="form-group" style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>Confirm New Password</label>
          <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
            placeholder="Confirm new password"
            style={{ width: '100%', padding: '10px 14px', fontSize: 13, background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 8 }} />
        </div>

        {pwMsg && (
          <div style={{
            padding: '8px 14px', borderRadius: 8, fontSize: 12, marginBottom: 12,
            background: pwMsg.type === 'error' ? 'rgba(248,81,73,0.1)' : 'rgba(63,185,80,0.1)',
            color: pwMsg.type === 'error' ? 'var(--accent-red)' : 'var(--accent-green)',
            border: `1px solid ${pwMsg.type === 'error' ? 'rgba(248,81,73,0.2)' : 'rgba(63,185,80,0.2)'}`,
          }}>
            {pwMsg.text}
          </div>
        )}

        <button className="btn btn-primary" onClick={handleChangePassword} disabled={pwSaving}
          style={{ padding: '10px 24px', fontSize: 13 }}>
          {pwSaving ? 'Changing...' : 'Change Password'}
        </button>
      </div>

      {/* Phone Number */}
      <div className="card" style={{ maxWidth: 700, marginBottom: 12 }}>
        <div className="section-title">Change Phone Number</div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>Phone Number</label>
          <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
            placeholder="+61451608308"
            style={{ width: '100%', padding: '10px 14px', fontSize: 13, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 8 }} />
        </div>
      </div>

      {/* Time Zone */}
      <div className="card" style={{ maxWidth: 700, marginBottom: 12 }}>
        <div className="section-title">Change Time Zone</div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>Time Zone</label>
          <select value={timezone} onChange={e => setTimezone(e.target.value)}
            style={{ width: '100%', fontSize: 13, padding: '10px 14px' }}>
            {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
      </div>


      {/* Theme */}
      <div className="card" style={{ maxWidth: 700, marginBottom: 12 }}>
        <div className="section-title">Theme</div>
        <p style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 16 }}>
          Themes are not available on Internet Explorer and some versions of Edge.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
          {THEMES.map(t => {
            const active = theme === t.id;
            return (
              <button key={t.id} onClick={() => handleThemeChange(t.id)} style={{
                background: 'none', border: `2px solid ${active ? 'var(--accent-blue)' : 'var(--border)'}`,
                borderRadius: 12, padding: 12, cursor: 'pointer', textAlign: 'left',
                transition: 'border-color 0.15s', boxShadow: active ? '0 0 0 3px rgba(88,166,255,0.15)' : 'none',
              }}>
                {/* Mini preview */}
                <div style={{
                  background: t.colors.bg, border: `1px solid ${t.colors.border}`,
                  borderRadius: 6, padding: 8, marginBottom: 10, height: 60, display: 'flex', gap: 4,
                }}>
                  <div style={{ width: 20, background: t.colors.card, borderRadius: 3, border: `1px solid ${t.colors.border}`, flexShrink: 0 }}>
                    {[0,1,2].map(i => <div key={i} style={{ height: 4, margin: '5px 3px', background: i === 0 ? t.colors.accent : t.colors.border, borderRadius: 2 }} />)}
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div style={{ height: 5, width: '70%', background: t.colors.text, borderRadius: 2, opacity: 0.5 }} />
                    <div style={{ display: 'flex', gap: 3, flex: 1 }}>
                      {[1,0.6].map((o, i) => <div key={i} style={{ flex: 1, background: t.colors.card, border: `1px solid ${t.colors.border}`, borderRadius: 3, opacity: o }} />)}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{t.label}</span>
                  {active && <span style={{ fontSize: 9, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', background: 'rgba(88,166,255,0.15)', color: 'var(--accent-blue)', borderRadius: 10, padding: '2px 6px' }}>ACTIVE</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{t.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Email Reports — managed in Notifications */}
      <EmailReportsCard />

      {/* Save Account */}
      <div style={{ maxWidth: 700, marginBottom: 40 }}>
        <button className="btn btn-primary" onClick={handleSaveAccount} disabled={acctSaving}
          style={{ padding: '12px 32px', fontSize: 14 }}>
          {acctSaved ? 'Saved!' : acctSaving ? 'Saving...' : 'Save Account Settings'}
        </button>
      </div>
    </>
  );
}

/* ── Email Reports Card — compact status, full controls in Notifications ── */
function EmailReportsCard() {
  const [weekly, setWeekly] = useState(true);
  const [monthly, setMonthly] = useState(true);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    notificationsAPI.get().then(d => {
      setWeekly(d.notify_weekly_report ?? true);
      setMonthly(d.notify_monthly_report ?? true);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  if (!loaded) return null;

  return (
    <div className="card" style={{ maxWidth: 700, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div className="section-title" style={{ marginBottom: 0 }}>Email Reports</div>
        <a href="/notifications" style={{ fontSize: 12, color: 'var(--accent-blue)', textDecoration: 'none', fontWeight: 500 }}>
          Manage in Notifications →
        </a>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        {[
          { label: 'Weekly', desc: 'Every Monday', enabled: weekly },
          { label: 'Monthly', desc: '1st of month', enabled: monthly },
        ].map(item => (
          <div key={item.label} style={{
            flex: 1, padding: '12px 16px', background: 'var(--bg-secondary)',
            border: `1px solid ${item.enabled ? 'rgba(34,197,94,0.3)' : 'var(--border)'}`,
            borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.enabled ? 'var(--accent-green)' : 'var(--text-muted)', flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{item.label} Report</div>
              <div style={{ fontSize: 11, color: item.enabled ? 'var(--accent-green)' : 'var(--text-muted)', marginTop: 1 }}>
                {item.enabled ? `Enabled · ${item.desc}` : 'Disabled'}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════
   TAB 2: BILLING & DOMAINS
   ════════════════════════════════════════ */
/* ── Invoice View Modal ── */
function InvoiceModal({ invoice, onClose }) {
  if (!invoice) return null;
  const fmtDate = (ts) => ts ? new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
  const fmtAmount = (amt, cur) => {
    const val = (amt || 0) / 100;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: (cur || 'usd').toUpperCase() }).format(val);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
        maxWidth: 600, width: '100%', maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Invoice {invoice.number || ''}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {fmtDate(invoice.date)}
              <span style={{
                marginLeft: 8, padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                background: invoice.status === 'paid' ? 'rgba(63,185,80,0.1)' : 'rgba(210,153,34,0.1)',
                color: invoice.status === 'paid' ? 'var(--accent-green)' : 'var(--accent-orange)',
              }}>{(invoice.status || '').toUpperCase()}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>&times;</button>
        </div>

        {/* Details */}
        <div style={{ padding: '20px 24px' }}>
          {invoice.customer_name && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 1, marginBottom: 4 }}>Bill To</div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{invoice.customer_name}</div>
              {invoice.customer_email && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{invoice.customer_email}</div>}
            </div>
          )}

          <div style={{ display: 'flex', gap: 24, marginBottom: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Invoice Date</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{fmtDate(invoice.date)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Period</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{fmtDate(invoice.period_start)} - {fmtDate(invoice.period_end)}</div>
            </div>
            {invoice.due_date && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Due Date</div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{fmtDate(invoice.due_date)}</div>
              </div>
            )}
          </div>

          {/* Line items */}
          <div className="table-wrap" style={{ border: 'none', marginBottom: 16 }}>
            <table>
              <thead>
                <tr>
                  <th>Description</th>
                  <th style={{ textAlign: 'right' }}>Qty</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.lines || []).map((line, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: 12 }}>{line.description || 'Subscription'}</td>
                    <td style={{ textAlign: 'right', fontSize: 12, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>{line.quantity || 1}</td>
                    <td style={{ textAlign: 'right', fontSize: 12, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>{fmtAmount(line.amount, invoice.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div style={{ borderTop: '2px solid var(--border)', paddingTop: 12 }}>
            {invoice.subtotal != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
                <span style={{ color: 'var(--text-secondary)' }}>Subtotal</span>
                <span style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{fmtAmount(invoice.subtotal, invoice.currency)}</span>
              </div>
            )}
            {invoice.tax != null && invoice.tax > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
                <span style={{ color: 'var(--text-secondary)' }}>Tax</span>
                <span style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{fmtAmount(invoice.tax, invoice.currency)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 15, fontWeight: 700 }}>
              <span style={{ color: 'var(--text-primary)' }}>Total</span>
              <span style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--accent-blue)' }}>{fmtAmount(invoice.total || invoice.amount_paid || invoice.amount_due, invoice.currency)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          {invoice.invoice_pdf && (
            <a href={invoice.invoice_pdf} target="_blank" rel="noopener noreferrer"
              className="btn btn-primary" style={{ padding: '8px 20px', fontSize: 12, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Download PDF
            </a>
          )}
          {invoice.hosted_invoice_url && (
            <a href={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
              className="btn btn-ghost" style={{ padding: '8px 20px', fontSize: 12, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              View Invoice
            </a>
          )}
          <button onClick={onClose} className="btn btn-ghost" style={{ padding: '8px 20px', fontSize: 12 }}>Close</button>
        </div>
      </div>
    </div>
  );
}

function BillingTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [campaigns, setCampaigns] = useState([]);
  const [billingStatus, setBillingStatus] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [billing, setBilling] = useState({
    company_name: '', vat_id: '', address: '', city: '', country: '', zip: '', billing_email: '',
  });
  const [billingSaving, setBillingSaving] = useState(false);
  const [billingSaved, setBillingSaved] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [subscribing, setSubscribing] = useState('');
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, percent, label }
  const [promoMsg, setPromoMsg] = useState(null);
  const didSync = useRef(false);

  const handleApplyPromo = () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) {
      setPromoMsg({ type: 'error', text: 'Enter a promo code' });
      return;
    }
    const found = PROMO_CODES[code];
    if (!found) {
      setAppliedPromo(null);
      setPromoMsg({ type: 'error', text: 'Invalid promo code' });
      return;
    }
    setAppliedPromo({ code, ...found });
    setPromoMsg({ type: 'success', text: `${found.label} applied — will be used at checkout.` });
  };

  const handleClearPromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
    setPromoMsg(null);
  };

  const priceFor = (plan) => appliedPromo
    ? applyPromoPrice(plan.basePrice, appliedPromo.percent)
    : `$${plan.basePrice}`;

  const loadStatus = () => billingAPI.getStatus().then(s => { setBillingStatus(s); setBillingLoading(false); }).catch(() => setBillingLoading(false));

  useEffect(() => {
    apiGet('/api/campaigns/').then(setCampaigns).catch(() => {});
    setInvoicesLoading(true);
    billingAPI.getInvoices(24).then(setInvoices).catch(() => {}).finally(() => setInvoicesLoading(false));
    apiGet('/api/auth/account').then(d => {
      const saved = d.billing || {};
      setBilling(b => ({
        ...b,
        ...saved,
        billing_email: saved.billing_email || auth.currentUser?.email || '',
      }));
    }).catch(() => {});

    // Load billing status, then auto-sync from Stripe if no active subscription found
    billingAPI.getStatus().then(status => {
      setBillingStatus(status);
      setBillingLoading(false);
      const hasSub = status?.subscription && !['canceled', 'unpaid'].includes(status.subscription?.status);
      if (!hasSub && !didSync.current) {
        didSync.current = true;
        billingAPI.syncPlan()
          .then(r => {
            if (r.plan && r.plan !== 'free') {
              window.location.href = '/dashboard';
            }
          })
          .catch(() => {});
      }
    }).catch(() => { setBillingLoading(false); });

    // Clean up ?billing=success from URL if present
    if (searchParams.get('billing') === 'success') {
      setSearchParams(p => { p.delete('billing'); return p; });
    }

    // Auto-apply promo code from URL (?promo=CLICK10 etc.)
    const urlPromo = (searchParams.get('promo') || '').toUpperCase();
    if (urlPromo && PROMO_CODES[urlPromo]) {
      setPromoInput(urlPromo);
      setAppliedPromo({ code: urlPromo, ...PROMO_CODES[urlPromo] });
      setPromoMsg({ type: 'success', text: `${PROMO_CODES[urlPromo].label} applied from link.` });
      setSearchParams(p => { p.delete('promo'); return p; });
    }
  }, []);

  const handleViewInvoice = async (inv) => {
    setSelectedInvoice(inv);
    try {
      const detail = await billingAPI.getInvoice(inv.id);
      setInvoiceDetail(detail);
    } catch (_) {
      // Fallback to the list data
      setInvoiceDetail(inv);
    }
  };

  const handleSaveBilling = async () => {
    setBillingSaving(true);
    try {
      await billingAPI.updateDetails(billing);
      setBillingSaved(true);
      setTimeout(() => setBillingSaved(false), 3000);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save billing details. Please try again.');
    }
    setBillingSaving(false);
  };

  const handleUpdatePayment = async () => {
    try {
      const r = await billingAPI.createPortal();
      if (r.portal_url) window.location.href = r.portal_url;
    } catch (_) {
      alert('Unable to open billing portal. Please check your Stripe configuration.');
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await billingAPI.cancelSubscription();
      setBillingStatus(s => ({
        ...s,
        subscription: { ...s?.subscription, cancel_at_period_end: true },
      }));
      setShowCancelConfirm(false);
    } catch (_) {
      alert('Failed to cancel subscription');
    }
    setCancelling(false);
  };

  const handleReactivate = async () => {
    try {
      await billingAPI.reactivate();
      setBillingStatus(s => ({
        ...s,
        subscription: { ...s?.subscription, cancel_at_period_end: false },
      }));
    } catch (_) {}
  };

  const handleChangePlan = async (plan) => {
    if (!plan.priceId) {
      alert('Payment not yet configured. Please contact support.');
      return;
    }
    if (!window.confirm(`Switch to the ${plan.name} plan? Your current subscription will be replaced immediately.`)) return;
    setSubscribing(plan.id);
    try {
      const r = await billingAPI.changePlan(plan.priceId, appliedPromo?.code);
      if (r.comped) {
        alert(`Plan changed to ${plan.name} (free via promo).`);
        window.location.href = '/dashboard';
        return;
      }
      if (r.approve_url) {
        window.location.href = r.approve_url;
        return;
      }
      await loadStatus();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to change plan. Please try again.');
    }
    setSubscribing('');
  };

  const handleSubscribe = async (plan) => {
    const priceId = plan.priceId;
    if (!priceId) {
      alert('Payment not yet configured. Please contact support.');
      return;
    }
    setSubscribing(plan.id);
    try {
      const r = await billingAPI.createCheckout({ price_id: priceId, interval: 'month', discount_code: appliedPromo?.code });
      if (r.comped) {
        alert(`Success — ${plan.name} plan activated free with promo code.`);
        window.location.href = '/dashboard';
        return;
      }
      if (r.discount_warning) alert(r.discount_warning);
      if (r.checkout_url) window.location.href = r.checkout_url;
    } catch (e) {
      console.error('[Checkout] error', { status: e?.response?.status, data: e?.response?.data, message: e?.message });
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      alert(detail ? `[${status}] ${detail}` : (e?.message || 'Failed to start checkout. Please try again.'));
    }
    setSubscribing('');
  };

  // Helpers
  const domains = [...new Set(campaigns.map(c => c.domain).filter(Boolean))];
  const sub = billingStatus?.subscription;
  const pm = billingStatus?.payment_method;
  const fmtDate = (ts) => ts ? new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
  const fmtAmount = (amt, cur) => {
    const val = (amt || 0) / 100;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: (cur || 'usd').toUpperCase() }).format(val);
  };

  const BillingRow = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{value || '-'}</span>
    </div>
  );

  return (
    <>
      {/* Invoice Modal */}
      {selectedInvoice && <InvoiceModal invoice={invoiceDetail || selectedInvoice} onClose={() => { setSelectedInvoice(null); setInvoiceDetail(null); }} />}

      {/* ── Promo Code ── */}
      <div className="card" style={{ maxWidth: 960, marginBottom: 12 }}>
        <div className="section-title">Promo Code</div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.6 }}>
          Have a promo code? Enter it below to see discounted pricing. The code will be applied at checkout.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            value={promoInput}
            onChange={e => setPromoInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleApplyPromo()}
            placeholder="e.g. CLICK10"
            disabled={!!appliedPromo}
            style={{
              flex: '1 1 240px', maxWidth: 320, padding: '10px 14px', fontSize: 13,
              fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', textTransform: 'none',
              background: 'var(--bg-primary)', color: 'var(--text-primary)',
              border: '1px solid var(--border)', borderRadius: 8,
              opacity: appliedPromo ? 0.6 : 1,
            }}
          />
          {appliedPromo ? (
            <button onClick={handleClearPromo} className="btn btn-ghost"
              style={{ padding: '10px 20px', fontSize: 13, borderColor: 'var(--accent-red)', color: 'var(--accent-red)' }}>
              Remove
            </button>
          ) : (
            <button onClick={handleApplyPromo} className="btn btn-ghost"
              style={{ padding: '10px 20px', fontSize: 13, borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)' }}>
              Apply
            </button>
          )}
        </div>

        {promoMsg && (
          <div style={{
            marginTop: 12, padding: '8px 14px', borderRadius: 8, fontSize: 12,
            background: promoMsg.type === 'error' ? 'rgba(248,81,73,0.1)' : 'rgba(63,185,80,0.1)',
            color: promoMsg.type === 'error' ? 'var(--accent-red)' : 'var(--accent-green)',
            border: `1px solid ${promoMsg.type === 'error' ? 'rgba(248,81,73,0.2)' : 'rgba(63,185,80,0.2)'}`,
          }}>
            {promoMsg.text}
          </div>
        )}

        {appliedPromo && (
          <div style={{
            marginTop: 12, padding: '10px 14px', borderRadius: 8, fontSize: 12,
            background: 'rgba(63,185,80,0.06)', border: '1px solid rgba(63,185,80,0.2)',
            color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ fontSize: 11, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', background: 'var(--accent-green)', color: '#fff', padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>
              {appliedPromo.code}
            </span>
            <span>{appliedPromo.label} — new prices shown below.</span>
          </div>
        )}

      </div>

      {/* ── Current Plan (shown when subscribed) ── */}
      {billingLoading && (
        <div className="card" style={{ maxWidth: 960, marginBottom: 12, textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: 13 }}>
          Loading billing status...
        </div>
      )}
      {!billingLoading && sub && (
        <div className="card" style={{ maxWidth: 960, marginBottom: 12 }}>
          <div className="section-title">Current Plan</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginTop: 8 }}>
            {PLANS.map(plan => {
              const isCurrent = billingStatus?.plan?.toLowerCase() === plan.id;
              return (
                <div key={plan.id} style={{
                  border: isCurrent ? '2px solid var(--accent-blue)' : '1px solid var(--border)',
                  borderRadius: 14, padding: '20px 24px', position: 'relative',
                  background: isCurrent ? 'rgba(9,105,218,0.04)' : 'var(--bg-secondary)',
                }}>
                  {isCurrent && (
                    <div style={{
                      position: 'absolute', top: -12, right: 20,
                      background: 'var(--accent-blue)', color: '#fff',
                      fontSize: 11, fontWeight: 700, padding: '3px 12px', borderRadius: 20,
                    }}>Current Plan</div>
                  )}
                  {plan.badge && !isCurrent && (
                    <div style={{
                      position: 'absolute', top: -12, right: 20,
                      background: 'var(--accent-orange)', color: '#fff',
                      fontSize: 11, fontWeight: 700, padding: '3px 12px', borderRadius: 20,
                    }}>{plan.badge}</div>
                  )}
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{plan.name}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                    {appliedPromo && (
                      <span style={{ fontSize: 14, color: 'var(--text-muted)', textDecoration: 'line-through', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>${plan.basePrice}</span>
                    )}
                    <span style={{ fontSize: 28, fontWeight: 800, color: 'var(--accent-orange)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>{priceFor(plan)}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>/month</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                    {plan.features.slice(0, 4).map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                        <span style={{ color: 'var(--accent-green)', fontWeight: 700 }}>✓</span>{f}
                      </div>
                    ))}
                  </div>
                  {isCurrent ? (
                    <button disabled style={{
                      width: '100%', padding: '9px 0', borderRadius: 8, border: '1px solid var(--border)',
                      background: 'var(--bg-primary)', color: 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'default',
                    }}>Current Plan</button>
                  ) : (
                    <button onClick={() => handleChangePlan(plan)} disabled={!!subscribing} className="btn btn-ghost" style={{
                      width: '100%', justifyContent: 'center', padding: '9px 0', fontSize: 13, fontWeight: 600,
                      borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)',
                    }}>
                      {subscribing === plan.id ? 'Switching...' : `Change to ${plan.name}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
            Plan changes take effect immediately. Your billing cycle carries over.
          </div>
        </div>
      )}

      {/* ── Plan Selection (shown when no active subscription) ── */}
      {!billingLoading && !sub && (
        <div className="card" style={{ maxWidth: 960, marginBottom: 12 }}>
          <div className="section-title">Choose a Plan</div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.6 }}>
            Choose a plan to get started. Cancel anytime.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {PLANS.map(plan => (
              <div key={plan.id} style={{
                border: plan.id === 'pro' ? '2px solid var(--accent-orange)' : '1px solid var(--border)',
                borderRadius: 14, padding: '24px 24px', position: 'relative', background: 'var(--bg-secondary)',
              }}>
                {plan.badge && (
                  <div style={{
                    position: 'absolute', top: -12, right: 20,
                    background: 'var(--accent-orange)', color: '#fff',
                    fontSize: 11, fontWeight: 700, padding: '3px 12px', borderRadius: 20,
                  }}>{plan.badge}</div>
                )}
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>{plan.name}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
                  {appliedPromo && (
                    <span style={{ fontSize: 16, color: 'var(--text-muted)', textDecoration: 'line-through', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>${plan.basePrice}</span>
                  )}
                  <span style={{ fontSize: 32, fontWeight: 800, color: 'var(--accent-orange)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>
                    {priceFor(plan)}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>/month</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                  {plan.features.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
                      <span style={{ color: 'var(--accent-green)', fontWeight: 700, fontSize: 14 }}>✓</span>
                      {f}
                    </div>
                  ))}
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => handleSubscribe(plan)}
                  disabled={!!subscribing}
                  style={{
                    width: '100%', justifyContent: 'center', padding: '10px 0', fontSize: 13, fontWeight: 600,
                    background: plan.id === 'pro' ? 'var(--accent-orange)' : undefined,
                    borderColor: plan.id === 'pro' ? 'var(--accent-orange)' : undefined,
                  }}
                >
                  {subscribing === plan.id ? 'Redirecting...' : 'Subscribe Now'}
                </button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
            🔒 Secured by Lemon Squeezy · Cancel anytime
          </div>
        </div>
      )}

      {/* Domains Table */}
      <div className="card" style={{ maxWidth: 900, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="section-title" style={{ marginBottom: 0 }}>Domains</div>
          <button className="btn btn-ghost" style={{ padding: '6px 16px', fontSize: 12, borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)' }}>
            Add a New Domain
          </button>
        </div>
        <div className="table-wrap" style={{ border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>DOMAIN</th>
                <th>DOMAIN PLAN</th>
                <th>PAYMENT PROFILE</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {domains.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>No domains yet. Create a campaign to add a domain.</td></tr>
              ) : domains.map((domain, i) => (
                <tr key={i}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 500, fontSize: 13 }}>{domain}</span>
                      <span style={{ color: 'var(--accent-red)', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>&times;</span>
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {sub ? 'Pro' : 'Free'}
                    {!sub && <span style={{ color: 'var(--text-muted)', fontSize: 10 }}> (awaiting setup)</span>}
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>
                    {sub?.id || '-'}
                  </td>
                  <td>
                    <button className="btn btn-ghost" style={{ padding: '4px 14px', fontSize: 11, borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)' }}>
                      Upgrade Plan
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>


      {/* Billing Details - 3 column layout */}
      <div className="card" style={{ maxWidth: 900, marginBottom: 12 }}>
        <div className="section-title">Billing Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24 }}>

          {/* Business Information */}
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Business Information</div>
            {['company_name', 'vat_id', 'address', 'city', 'country', 'zip', 'billing_email'].map(field => (
              <div className="form-group" key={field} style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {{ company_name: 'Company Name', vat_id: 'Company VAT ID', address: 'Address', city: 'City', country: 'Country', zip: 'ZIP Code', billing_email: 'Email Invoices to' }[field]}
                </label>
                <input value={billing[field] || ''}
                  onChange={e => setBilling(b => ({ ...b, [field]: e.target.value }))}
                  placeholder={{ company_name: 'Company Name', vat_id: 'VAT ID', address: 'Street Address', city: 'City', country: 'Country', zip: 'ZIP Code', billing_email: 'billing@company.com' }[field]}
                  style={{ fontSize: 12, padding: '8px 10px', ...(field === 'billing_email' ? { fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' } : {}) }} />
              </div>
            ))}
            <button className="btn btn-ghost" onClick={handleSaveBilling} disabled={billingSaving}
              style={{ padding: '8px 20px', fontSize: 12, marginTop: 4, borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)' }}>
              {billingSaved ? 'Saved!' : billingSaving ? 'Saving...' : 'Update Billing'}
            </button>
          </div>

          {/* Payment Details */}
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Payment Details</div>
            <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border)', textAlign: 'center', marginBottom: 12 }}>
              {pm ? (
                <div style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 500 }}>
                  PayPal
                </div>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No payment method on file</div>
              )}
            </div>
            <button onClick={handleUpdatePayment} className="btn btn-ghost"
              style={{ width: '100%', justifyContent: 'center', padding: '8px 16px', fontSize: 12, borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)' }}>
              Update Credit Card
            </button>
          </div>

          {/* Subscription Details */}
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Subscription Details</div>
            <BillingRow label="Status" value={
              <span style={{ color: sub?.status === 'active' ? 'var(--accent-green)' : 'var(--accent-orange)', fontWeight: 600, textTransform: 'capitalize' }}>
                {sub?.status || 'No subscription'}
                {sub?.cancel_at_period_end && <span style={{ color: 'var(--accent-orange)', fontSize: 10, marginLeft: 4 }}>(cancels at period end)</span>}
              </span>
            } />
            <BillingRow label="Amount" value={sub ? fmtAmount(sub.plan?.amount, sub.plan?.currency) : '-'} />
            <BillingRow label="Interval" value={sub?.plan?.interval || '-'} />
            <BillingRow label="Prev. Payment Date" value={fmtDate(sub?.current_period_start)} />
            <BillingRow label="Next Payment Date" value={fmtDate(sub?.current_period_end)} />
            <BillingRow label="Domains on this card" value={String(domains.length)} />
            <BillingRow label="Plan Usage" value={`${campaigns.length} / 25,000`} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>Reference</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', wordBreak: 'break-all' }}>{sub?.id || '-'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice History */}
      <div className="card" style={{ maxWidth: 900, marginBottom: 12 }}>
        <div className="section-title">Invoice History</div>
        {invoicesLoading ? (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>Loading invoices...</div>
        ) : invoices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>No invoices yet.</div>
        ) : (
          <div className="table-wrap" style={{ border: 'none' }}>
            <table>
              <thead>
                <tr>
                  <th>INVOICE DATE</th>
                  <th>NUMBER</th>
                  <th>AMOUNT</th>
                  <th>STATUS</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: 13 }}>{fmtDate(inv.date)}</td>
                    <td style={{ fontSize: 12, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>{inv.number || '-'}</td>
                    <td style={{ fontSize: 12, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>{fmtAmount(inv.amount_paid || inv.amount_due, inv.currency)}</td>
                    <td>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                        background: inv.status === 'paid' ? 'rgba(63,185,80,0.1)' : inv.status === 'open' ? 'rgba(210,153,34,0.1)' : 'rgba(248,81,73,0.1)',
                        color: inv.status === 'paid' ? 'var(--accent-green)' : inv.status === 'open' ? 'var(--accent-orange)' : 'var(--accent-red)',
                        textTransform: 'none',
                      }}>{inv.status}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => handleViewInvoice(inv)} className="btn btn-ghost"
                          style={{ padding: '4px 12px', fontSize: 11, borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)' }}>
                          View Invoice
                        </button>
                        {inv.invoice_pdf && (
                          <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer"
                            className="btn btn-ghost" style={{ padding: '4px 12px', fontSize: 11, borderColor: 'var(--accent-blue)', color: 'var(--accent-blue)', textDecoration: 'none' }}>
                            Download
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Contact Account Manager */}
      <div className="card" style={{ maxWidth: 900, marginBottom: 12 }}>
        <div className="section-title">Contact Your Account Manager</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <a href="mailto:support@clicksaver.app" className="btn btn-ghost" style={{ padding: '8px 20px', fontSize: 12, borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)', textDecoration: 'none' }}>
            Send Email
          </a>
        </div>
      </div>

      {/* Cancel Account */}
      <div className="card" style={{ maxWidth: 900, marginBottom: 40 }}>
        <div className="section-title">Cancel ClickSaver Account</div>
        {sub?.cancel_at_period_end ? (
          <div style={{ padding: '16px', background: 'rgba(210,153,34,0.06)', borderRadius: 8, border: '1px solid rgba(210,153,34,0.2)' }}>
            <div style={{ fontSize: 13, color: 'var(--accent-orange)', marginBottom: 8 }}>
              Your subscription is set to cancel on <strong>{fmtDate(sub.current_period_end)}</strong>.
            </div>
            <button onClick={handleReactivate} className="btn btn-ghost"
              style={{ padding: '8px 20px', fontSize: 12, borderColor: 'var(--accent-green)', color: 'var(--accent-green)' }}>
              Reactivate Subscription
            </button>
          </div>
        ) : !showCancelConfirm ? (
          <button onClick={() => setShowCancelConfirm(true)}
            style={{ background: 'none', border: 'none', color: 'var(--accent-red)', fontSize: 13, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
            Cancel Account...
          </button>
        ) : (
          <div style={{ padding: '16px', background: 'rgba(248,81,73,0.06)', borderRadius: 8, border: '1px solid rgba(248,81,73,0.2)' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent-red)', marginBottom: 8 }}>
              Are you sure you want to cancel?
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.5 }}>
              This will cancel your subscription at the end of the current billing period. Your data will be retained for 30 days.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleCancel} disabled={cancelling} className="btn btn-ghost"
                style={{ padding: '8px 20px', fontSize: 12, borderColor: 'var(--accent-red)', color: 'var(--accent-red)' }}>
                {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
              <button className="btn btn-ghost" onClick={() => setShowCancelConfirm(false)}
                style={{ padding: '8px 20px', fontSize: 12 }}>
                Keep My Account
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ════════════════════════════════════════
   TAB 3: SHARE ACCOUNT
   ════════════════════════════════════════ */
function ShareTab() {
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [sharedUsers, setSharedUsers] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    apiGet('/api/auth/shared-users').then(setSharedUsers).catch(() => {});
  }, []);

  const handleShare = async () => {
    if (!email.trim() || !email.includes('@')) return setMsg({ type: 'error', text: 'Please enter a valid email address' });
    setSharing(true);
    setMsg(null);
    try {
      const r = await apiPost('/api/auth/share', { email: email.trim(), role: 'viewer' });
      setSharedUsers(r.shared_users || []);
      setEmail('');
      setShowForm(false);
      setMsg({ type: 'success', text: `Account shared with ${email.trim()}` });
    } catch (e) {
      setMsg({ type: 'error', text: 'Failed to share account' });
    }
    setSharing(false);
  };

  const handleRemove = async (emailToRemove) => {
    if (!window.confirm(`Remove access for ${emailToRemove}?`)) return;
    try {
      const r = await apiDelete(`/api/auth/share/${encodeURIComponent(emailToRemove)}`);
      setSharedUsers(r.shared_users || []);
    } catch (_) {}
  };

  return (
    <div className="card" style={{ maxWidth: 800 }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Share Account</div>

      {/* Share button / form */}
      {!showForm ? (
        <button onClick={() => setShowForm(true)} className="btn btn-ghost"
          style={{ padding: '10px 24px', fontSize: 13, borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)', marginBottom: 12 }}>
          Share Account with New User
        </button>
      ) : (
        <div style={{
          padding: '20px', background: 'var(--bg-secondary)', borderRadius: 10,
          border: '1px solid var(--border)', marginBottom: 12,
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>
            Share Account with New User
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Enter email address"
              autoFocus
              style={{
                flex: '1 1 300px', padding: '10px 14px', fontSize: 13,
                background: 'var(--bg-primary)', color: 'var(--text-primary)',
                border: '1px solid var(--border)', borderRadius: 8,
              }}
              onKeyDown={e => e.key === 'Enter' && handleShare()}
            />
            <button onClick={handleShare} disabled={sharing} className="btn btn-ghost"
              style={{ padding: '10px 24px', fontSize: 13, borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)', flexShrink: 0 }}>
              {sharing ? 'Sharing...' : 'Share'}
            </button>
            <button onClick={() => { setShowForm(false); setEmail(''); setMsg(null); }} className="btn btn-ghost"
              style={{ padding: '10px 16px', fontSize: 13, flexShrink: 0 }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {msg && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, fontSize: 12, marginBottom: 16,
          background: msg.type === 'error' ? 'rgba(248,81,73,0.1)' : 'rgba(63,185,80,0.1)',
          color: msg.type === 'error' ? 'var(--accent-red)' : 'var(--accent-green)',
          border: `1px solid ${msg.type === 'error' ? 'rgba(248,81,73,0.2)' : 'rgba(63,185,80,0.2)'}`,
        }}>
          {msg.text}
        </div>
      )}

      {/* Shared users list */}
      <div style={{
        fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 1,
        textTransform: 'none', marginBottom: 12, paddingBottom: 8,
        borderBottom: '1px solid var(--border)',
      }}>
        ACCOUNT SHARED WITH:
      </div>

      {sharedUsers.length === 0 ? (
        <div style={{ padding: '20px 0', color: 'var(--text-muted)', fontSize: 13 }}>
          No users have been added yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {sharedUsers.map((u, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 0', borderBottom: i < sharedUsers.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* Avatar */}
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'var(--accent-blue)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0,
                }}>
                  {(u.email || '?')[0].toUpperCase()}
                </div>
                <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{u.email}</span>
              </div>
              <button onClick={() => handleRemove(u.email)}
                style={{
                  background: 'none', border: 'none', color: 'var(--accent-red)',
                  cursor: 'pointer', fontSize: 18, padding: '0 4px', lineHeight: 1,
                  opacity: 0.6,
                }}
                title="Remove user"
              >&times;</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════
   MAIN SETTINGS PAGE
   ════════════════════════════════════════ */
export default function Settings({ theme, setTheme }) {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return TABS.some(t => t.id === tab) ? tab : 'account';
  });

  return (
    <div>
      <div className="page-header">
        <h1>Settings</h1>
        <p>Manage your account, billing, and team access</p>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid var(--border)',
        overflowX: 'auto',
      }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            background: 'none', border: 'none', padding: '12px 24px', fontSize: 14, fontWeight: 600,
            cursor: 'pointer', whiteSpace: 'nowrap',
            color: activeTab === tab.id ? 'var(--accent-blue)' : 'var(--text-secondary)',
            borderBottom: `2px solid ${activeTab === tab.id ? 'var(--accent-blue)' : 'transparent'}`,
            marginBottom: -2, transition: 'all 0.15s',
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'account' && <AccountTab theme={theme} setTheme={setTheme} />}
      {activeTab === 'billing' && <BillingTab />}
    </div>
  );
}
